import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Award, Building2, Copy, KeyRound, MessageSquare, Plane, Plus, Send, Ship, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { Avatar, Card, CardTitle, btn, normalizePhone, isPhoneKey, normalizePhoneOnBlur } from "@/components/common";
import { CardsPanel } from "@/components/PaymentAuthorizations";
import { TripRow } from "@/components/TripCard";
import { cn } from "@/lib/utils";
import { daysUntil } from "@/lib/trip";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50 disabled:text-slate-400 sm:text-sm";
const lbl = "mb-1.5 block text-xs font-medium text-slate-500";

const STATUS = { not_invited: ["Not invited", "bg-slate-100 text-slate-600"], invited: ["Invite sent", "bg-amber-50 text-amber-700"], active: ["MyTrip active", "bg-emerald-50 text-emerald-700"] };
const TABS = [["contact", "Contact"], ["travel", "Travel"], ["trips", "Trips"], ["loyalty", "Loyalty"], ["payments", "Payments"], ["account", "Account"]];

function TripGroup({ title, trips, empty, testId }) {
  return (
    <Card data-testid={testId}>
      <CardTitle action={<span className="text-xs text-slate-400">{trips.length}</span>}>{title}</CardTitle>
      <div className="p-2 sm:p-3">
        {trips.map((t) => <TripRow key={t.id} trip={t} to={t.status === "QUOTE" ? `/agent/quotes/${t.id}` : `/agent/trips/${t.id}`} />)}
        {!trips.length && <p className="px-3 py-4 text-sm text-slate-500">{empty}</p>}
      </div>
    </Card>
  );
}

function InviteButton({ c }) {
  const { inviteClient } = useDemo();
  const [res, setRes] = useState(null);
  const invite = async () => {
    const r = await inviteClient(c.id);
    if (r) {
      setRes({ link: `${window.location.origin}/invite/${r.token}`, emailed: r.emailed });
      if (r.emailed) toast.success(`Invite emailed to ${c.email}`);
    }
  };
  return (
    <>
      <button onClick={invite} data-testid="client-send-invite-btn" className={btn.secondary}><Send className="h-4 w-4" />{c.status === "invited" ? "Resend Invite" : "Send Invite"}</button>
      <Dialog open={!!res} onOpenChange={() => setRes(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-2xl p-5 sm:p-7" data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">{res?.emailed ? "Invite sent" : "Invite ready"}</DialogTitle>
            <DialogDescription>{res?.emailed ? `We emailed the invite to ${c.email}. You can also copy the link below as a backup — it works once and expires in 14 days.` : `We couldn't email this invite right now. Copy the link below and send it to ${c.email}. It works once and expires in 14 days.`}</DialogDescription>
          </DialogHeader>
          <p className="break-all rounded-xl bg-slate-50 p-4 font-mono text-xs text-navy-900" data-testid="invite-link-text">{res?.link}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => { navigator.clipboard?.writeText(res?.link); toast.success("Invite link copied"); }} className={btn.secondary} data-testid="invite-link-copy"><Copy className="h-4 w-4" />Copy Link</button>
            <button onClick={() => setRes(null)} className={btn.primary}>Done</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditForm({ client, testId, fields, readonly = [] }) {
  const { updateClient } = useDemo();
  const [v, setV] = useState(() => Object.fromEntries(fields.map(([k]) => [k, client[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const dirty = fields.some(([k]) => (v[k] ?? "") !== (client[k] ?? ""));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await updateClient(client.id, v);
    setBusy(false);
    if (ok) toast.success("Saved");
  };
  return (
    <Card data-testid={testId}>
      <form onSubmit={save} className="grid gap-4 p-5 sm:grid-cols-2 sm:p-6">
        {readonly.map(([l, val]) => (
          <label key={l}><span className={lbl}>{l}</span><input className={field} value={val || "—"} disabled /></label>
        ))}
        {fields.map(([k, l, type = "text", full]) => (
          <label key={k} className={full || type === "textarea" ? "sm:col-span-2" : ""}>
            <span className={lbl}>{l}</span>
            {type === "textarea"
              ? <textarea rows={2} className={`${field} h-auto py-2.5`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`client-field-${k}`} />
              : <input type={type} className={field} value={v[k]} onChange={(e) => setV({ ...v, [k]: isPhoneKey(k) ? normalizePhone(e.target.value) : e.target.value })} onBlur={isPhoneKey(k) ? normalizePhoneOnBlur((n) => setV((p) => ({ ...p, [k]: n }))) : undefined} data-testid={`client-field-${k}`} />}
          </label>
        ))}
        <div className="sm:col-span-2"><button disabled={!dirty || busy} className={btn.primary} data-testid={`${testId}-save`}>Save Changes</button></div>
      </form>
    </Card>
  );
}

function AccountTab({ client }) {
  const { resetClientPassword, deleteClient } = useDemo();
  const nav = useNavigate();
  const [creds, setCreds] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const active = client.status === "active";
  const reset = async () => { const r = await resetClientPassword(client.id); if (r) setCreds(r); };
  const remove = async () => { const r = await deleteClient(client.id); if (r) nav("/agent/clients"); };
  return (
    <Card data-testid="client-account-tab">
      <CardTitle>Account & security</CardTitle>
      <div className="space-y-5 p-5 sm:p-6">
        <div className="rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-navy-900">Reset MyTrip password</p>
          <p className="mb-3 mt-1 text-xs text-slate-500">You can never view a client's password. Reset it to generate a one-time temporary password to share — their old sessions sign out immediately.</p>
          {creds ? (
            <div className="space-y-3" data-testid="client-reset-creds">
              <pre className="whitespace-pre-wrap break-all rounded-lg bg-slate-50 p-3 font-mono text-sm text-navy-900">{`Email: ${creds.email}\nTemporary password: ${creds.tempPassword}`}</pre>
              <button onClick={() => { navigator.clipboard?.writeText(`Email: ${creds.email}\nTemporary password: ${creds.tempPassword}`); toast.success("Copied"); }} className={btn.secondary} data-testid="client-reset-copy"><Copy className="h-4 w-4" />Copy</button>
            </div>
          ) : (
            <button onClick={reset} disabled={!active} className={btn.secondary} data-testid="client-reset-btn"><KeyRound className="h-4 w-4" />{active ? "Reset password" : "No account yet"}</button>
          )}
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
          <p className="text-sm font-semibold text-red-700">Delete client</p>
          <p className="mb-3 mt-1 text-xs text-slate-500">Permanently removes {client.name}, their account, trips and messages. This can't be undone.</p>
          {confirm ? (
            <div className="flex flex-wrap gap-2" data-testid="client-delete-confirm">
              <button onClick={() => setConfirm(false)} className={btn.secondary}>Cancel</button>
              <button onClick={remove} className={`${btn.primary} bg-red-600 hover:bg-red-700`} data-testid="client-delete-confirm-btn"><Trash2 className="h-4 w-4" />Delete Permanently</button>
            </div>
          ) : (
            <button onClick={() => setConfirm(true)} className={`${btn.secondary} text-red-600 hover:bg-red-50`} data-testid="client-delete-btn"><Trash2 className="h-4 w-4" />Delete Client</button>
          )}
        </div>
      </div>
    </Card>
  );
}

const LOYALTY_PROGRAMS = {
  airline: ["Delta SkyMiles", "United MileagePlus", "American AAdvantage", "Southwest Rapid Rewards", "Alaska Mileage Plan", "JetBlue TrueBlue", "British Airways Executive Club", "Air Canada Aeroplan", "Lufthansa Miles & More", "Emirates Skywards"],
  cruise: ["Royal Caribbean Crown & Anchor Society", "Carnival VIFP Club", "Norwegian Latitudes Rewards", "Celebrity Captain's Club", "Princess Captain's Circle", "Holland America Mariner Society", "MSC Voyagers Club", "Disney Castaway Club"],
  hotel: ["Marriott Bonvoy", "Hilton Honors", "World of Hyatt", "IHG One Rewards", "Wyndham Rewards", "Choice Privileges", "Best Western Rewards", "Accor Live Limitless"],
};
const LOYALTY_CATS = [["airline", "Airline"], ["cruise", "Cruise"], ["hotel", "Hotel"]];
const LOYALTY_LABEL = { airline: "Airline", cruise: "Cruise", hotel: "Hotel" };
const LOYALTY_ICON = { airline: Plane, cruise: Ship, hotel: Building2 };

function AddLoyaltyDialog({ client, open, onOpenChange }) {
  const { addLoyalty } = useDemo();
  const [f, setF] = useState({ category: "airline", program: LOYALTY_PROGRAMS.airline[0], number: "" });
  const setCat = (e) => setF({ ...f, category: e.target.value, program: LOYALTY_PROGRAMS[e.target.value][0] });
  const submit = async (e) => {
    e.preventDefault();
    const r = await addLoyalty(client.id, f);
    if (r) { onOpenChange(false); setF({ category: "airline", program: LOYALTY_PROGRAMS.airline[0], number: "" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-5 sm:p-7" data-testid="add-loyalty-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Add Loyalty Account</DialogTitle>
          <DialogDescription>Store {client.name}'s airline, cruise or hotel rewards membership.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className={lbl}>Category</span>
            <select className={field} value={f.category} onChange={setCat} data-testid="loyalty-category-select">{LOYALTY_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label className="block"><span className={lbl}>Program</span>
            <select className={field} value={f.program} onChange={(e) => setF({ ...f, program: e.target.value })} data-testid="loyalty-program-select">{LOYALTY_PROGRAMS[f.category].map((pr) => <option key={pr} value={pr}>{pr}</option>)}</select></label>
          <label className="block"><span className={lbl}>Member number</span>
            <input className={field} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="e.g. 1234567890" data-testid="loyalty-number-input" /></label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
            <button className={btn.primary} data-testid="loyalty-submit">Add Account</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LoyaltyTab({ client }) {
  const { removeLoyalty } = useDemo();
  const [open, setOpen] = useState(false);
  const list = client.loyalty || [];
  return (
    <Card data-testid="client-loyalty-tab">
      <CardTitle action={<button onClick={() => setOpen(true)} className={btn.ghost} data-testid="add-loyalty-btn"><Plus className="h-4 w-4" />Add Account</button>}>Loyalty &amp; rewards</CardTitle>
      <div className="p-5 sm:p-6">
        {list.length ? (
          <ul className="space-y-2">
            {list.map((a) => {
              const Icon = LOYALTY_ICON[a.category] || Award;
              return (
                <li key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid={`loyalty-row-${a.id}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy-50 text-navy-700"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy-900">{a.program}</p>
                    <p className="truncate text-xs text-slate-500">{LOYALTY_LABEL[a.category]}{a.number ? ` · ${a.number}` : ""}</p>
                  </div>
                  <button onClick={() => removeLoyalty(client.id, a.id)} data-testid={`loyalty-remove-${a.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </li>
              );
            })}
          </ul>
        ) : <p className="text-sm text-slate-500">No loyalty accounts yet. Add airline, cruise and hotel rewards numbers here.</p>}
      </div>
      <AddLoyaltyDialog client={client} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

export default function ClientProfile() {
  const { id } = useParams();
  const { client, tripsFor } = useDemo();
  const [tab, setTab] = useState("contact");
  const c = client(id);
  if (!c) return <Navigate to="/agent/clients" replace />;
  const trips = tripsFor(id);
  const upcoming = trips.filter((t) => t.status === "BOOKED" && daysUntil(t.departure) >= 0).sort((a, b) => a.departure.localeCompare(b.departure));
  const past = trips.filter((t) => t.status === "COMPLETED" || (t.status === "BOOKED" && daysUntil(t.departure) < 0));
  const quotes = trips.filter((t) => t.status === "QUOTE");

  return (
    <div data-testid="client-profile">
      <Link to="/agent/clients" data-testid="back-to-clients" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-navy-900"><ArrowLeft className="h-4 w-4" />All Clients</Link>
      <Card className="mb-6 p-5 sm:p-7">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Avatar name={c.name} size="xl" className="hidden sm:grid" />
            <Avatar name={c.name} size="lg" className="sm:hidden" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl" data-testid="client-profile-name">{c.name}</h1>
                <span data-testid="client-invite-status" className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${STATUS[c.status || "not_invited"][1]}`}>{STATUS[c.status || "not_invited"][0]}</span>
              </div>
              <p className="mt-1 text-sm text-slate-500">{trips.length} {trips.length === 1 ? "trip" : "trips"} on file · Client since {c.since}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {c.status !== "active" && <InviteButton c={c} />}
            <Link to={`/agent/messages/conv-${c.id}`} data-testid="client-message-btn" className={btn.secondary}><MessageSquare className="h-4 w-4" />Message</Link>
            <Link to={`/agent/quotes?new=1&client=${c.id}`} data-testid="client-new-quote-btn" className={btn.primary}><Plus className="h-4 w-4" />New Quote</Link>
          </div>
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-6" data-testid="client-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`client-tab-${k}`} className={cn("rounded-lg px-1.5 py-1.5 text-center text-xs font-medium transition-colors sm:text-sm", tab === k ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}>{label}</button>
        ))}
      </div>

      {tab === "contact" && (
        <div className="max-w-3xl">
          <EditForm client={c} testId="client-contact-form"
            readonly={[["Email (sign in)", c.email], ["Client since", c.since]]}
            fields={[["phone", "Phone", "tel"], ["dob", "Date of birth", "date"], ["address", "Mailing address", "text", true], ["emergencyName", "Emergency contact name"], ["emergencyPhone", "Emergency contact phone", "tel"]]} />
        </div>
      )}
      {tab === "travel" && (
        <div className="max-w-3xl">
          <EditForm client={c} testId="client-travel-form"
            fields={[["passport", "Passport number"], ["passportCountry", "Issuing country"], ["passportIssued", "Issue date", "date"], ["passportExpires", "Expiration date", "date"], ["knownTraveler", "Known traveler number"], ["accessibility", "Accessibility requirements", "textarea"], ["dietary", "Dietary preferences", "textarea"], ["travelPrefs", "Travel preferences", "textarea"]]} />
        </div>
      )}
      {tab === "trips" && (
        <div className="grid gap-6 lg:grid-cols-2" data-testid="client-trips-tab">
          <TripGroup testId="client-upcoming-trips" title="Upcoming Trips" trips={upcoming} empty="No upcoming trips booked." />
          <TripGroup testId="client-open-quotes" title="Open Quotes" trips={quotes} empty="No open quotes." />
          <TripGroup testId="client-past-trips" title="Past Trips" trips={past} empty="No completed trips yet." />
        </div>
      )}
      {tab === "loyalty" && <div className="max-w-3xl"><LoyaltyTab client={c} /></div>}
      {tab === "payments" && <CardsPanel clientId={c.id} canManage />}
      {tab === "account" && <div className="max-w-3xl space-y-4"><AccountTab client={c} /></div>}
    </div>
  );
}
